import React, { useEffect, useRef, useState } from 'react';
import { FiMic, FiRefreshCw } from 'react-icons/fi';
import { useMedia } from '../../interview/context/MediaContext';
import { checkInterviewAudio } from '../utils/aiInterviewApi';
import { createRecognizer } from '../hooks/useSpeech';

const joinPcm = (chunks) => {
  const bytes = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const output = new Uint8Array(bytes);
  let offset = 0;
  chunks.forEach((chunk) => { output.set(new Uint8Array(chunk), offset); offset += chunk.byteLength; });
  return output.buffer;
};

const InterviewAudioCheck = ({ disabled = false, onComplete, onSkip }) => {
  const { capture, restartMic, setFrameSink } = useMedia();
  const chunks = useRef([]);
  const timer = useRef(null);
  const recordingRef = useRef(false);
  const mountedRef = useRef(true);
  const recognizer = useRef(null);
  const browserCaption = useRef('');
  const captionSource = useRef('unknown');
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recordingRef.current = false;
      if (timer.current) clearTimeout(timer.current);
      setFrameSink(null);
      try { recognizer.current?.abort(); } catch { /* recognition is optional */ }
    };
  }, [setFrameSink]);

  const stop = async () => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setFrameSink(null);
    try { recognizer.current?.stop(); } catch { /* recognition is optional */ }
    recognizer.current = null;
    setRecording(false);
    setBusy(true);
    try {
      if (!chunks.current.length) {
        throw new Error('No microphone audio was captured. Check your browser or device microphone settings, then retry.');
      }
      const metrics = capture.getMetrics();
      const response = await checkInterviewAudio(joinPcm(chunks.current), browserCaption.current,
        captionSource.current, {
          raw_peak: Number(metrics.rawPeak?.toFixed(3)) || 0,
          processed_peak: Number(metrics.processedPeak?.toFixed(3)) || 0,
          raw_clipped: !!metrics.rawClipped,
          processed_clipped: !!metrics.processedClipped,
          track_rate: metrics.settings?.sampleRate || null,
          echo_cancellation: metrics.settings?.echoCancellation ?? null,
          noise_suppression: metrics.settings?.noiseSuppression ?? null,
          device_hint: metrics.deviceHint || 'unknown',
        }, capture.lowQualityAudio);
      if (!mountedRef.current) return;
      const passed = response.heard_speech && response.understood && !!(response.transcript || '').trim()
        && !response.clipped && !metrics.rawClipped;
      setResult({ ...response, passed, browserCaption: browserCaption.current });
      setAttempts((count) => count + 1);
      onComplete?.(passed);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message || 'Microphone test is unavailable. Please retry.');
      setAttempts((count) => count + 1);
      onComplete?.(false);
    } finally { if (mountedRef.current) setBusy(false); }
  };

  const start = async () => {
    if (recordingRef.current || busy) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      await restartMic();
      if (!mountedRef.current) return;
      chunks.current = [];
      browserCaption.current = '';
      captionSource.current = 'unknown';
      capture.resetTurnMetrics();
      setFrameSink((frame) => chunks.current.push(frame));
      const rec = createRecognizer((text) => { browserCaption.current = text || ''; }, () => {});
      recognizer.current = rec;
      if (rec?.start('', capture.getStream()?.getAudioTracks()[0])) captionSource.current = rec.getAudioSource();
      recordingRef.current = true;
      setRecording(true);
      timer.current = setTimeout(stop, 8000);
    } catch (e) { if (mountedRef.current) setError(e.message || 'Microphone unavailable. Check browser settings and retry.'); }
    finally { if (mountedRef.current) setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <p className="font-semibold">Microphone test <span className="font-normal text-slate-500">(optional)</span></p>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">Say a sentence of your choice for a few seconds, then compare the transcript. Keep a neckband microphone near your mouth; if earbuds sound unclear, switch microphones in your device or browser settings and retest.</p>
      {recording && <div className="mt-2 h-2 overflow-hidden rounded bg-slate-200 dark:bg-slate-700">
        <div className="h-full bg-emerald-500" style={{ width: `${Math.round(capture.level * 100)}%` }} />
      </div>}
      {result && <p role="status" className={`mt-2 text-xs ${result.passed ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
        {result.passed ? 'Speech was detected. Compare the server transcript with what you said.' : 'The test could not confirm enough clear speech. You can retest or continue.'}
        {result.transcript && <span className="mt-1 block">Server heard: “{result.transcript}”</span>}
        {result.browserCaption && <span className="mt-1 block">Browser heard: “{result.browserCaption}”</span>}
      </p>}
      {error && <p role="alert" className="mt-2 text-xs text-rose-600">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={recording ? stop : start} disabled={busy || (disabled && !recording)} className="inline-flex items-center gap-2 rounded bg-indigo-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
          {recording ? <FiMic /> : <FiRefreshCw />}{recording ? 'Stop test' : busy ? 'Checking...' : attempts ? 'Retest microphone' : 'Test microphone'}
        </button>
        {onSkip && !recording && <button type="button" onClick={onSkip} disabled={busy}
          className="rounded border border-slate-300 px-3 py-2 text-xs font-semibold disabled:opacity-50 dark:border-slate-600">
          Skip test
        </button>}
      </div>
    </div>
  );
};

export default InterviewAudioCheck;
